import type { CustomerSession } from '@pf/bff-contract';
import { OperatorIdSchema, RoleSchema } from '@pf/contracts';
import { z } from 'zod';

/**
 * Console-local wire shapes — and *only* the ones no published contract carries.
 *
 * The rule this file obeys: **never hand-write a type some package already defines.** There are
 * two of those, and between them they cover everything the console talks to:
 *
 * - `@pf/contracts` — PaymentFlow's frozen API (`/v1`, `/ops/v1`, webhooks).
 * - `@pf/bff-contract` — the Cashier Profile, the customer area's entire vocabulary. Everything
 *   the customer screens send or receive now lives there, because those shapes are the seam a
 *   third party reimplements, not this application's private business.
 *
 * What remains here is genuinely console-local: shapes for surfaces that exist only because this
 * particular console exists, each composed from contract primitives rather than re-declaring them.
 *
 * 1. **Operator session** — SAD §11 specifies session cookies + Argon2id + mandatory TOTP + the
 *    four RBAC roles, but `/ops/v1` has no login endpoint in the OpenAPI document, because the
 *    session plane is not part of the participant-facing API contract.
 * 2. **Dashboard metrics** — SAD §13 names the KPIs; they are served as a Prometheus scrape and
 *    Grafana projections, not as a versioned JSON contract. The console needs a JSON view of
 *    them, so it defines one here.
 * 3. **Review-queue projection** and **registered payee accounts** — operator read models.
 *
 * Every schema below is exported so the MSW handlers can validate their own responses against
 * it, exactly as they validate contract responses against `@pf/contracts`.
 */

// ---------------------------------------------------------------------------------------------
// 1 · Operator session (SAD §11 Plane B)
// ---------------------------------------------------------------------------------------------

/** Which of the console's two areas a signed-in identity lands in (D-I7). */
export const ConsoleAreaSchema = z.enum(['customer', 'operator']);
/** Inferred type of {@link ConsoleAreaSchema}. */
export type ConsoleArea = z.infer<typeof ConsoleAreaSchema>;

/** The authenticated identity the console holds after login. */
export const SessionSchema = z
  .object({
    operator_id: OperatorIdSchema.nullable(),
    display_name: z.string().min(1),
    /**
     * `null` is possible on a customer session and never on an operator one: SAD §11 makes email
     * the operator's login identity, whereas the Cashier Profile lets a website authenticate its
     * customers however it likes — an account number, a phone, an existing SSO subject.
     */
    email: z.string().email().nullable(),
    area: ConsoleAreaSchema,
    /** `null` for a customer session — RBAC applies to the operator plane only. */
    role: RoleSchema.nullable(),
    participant: z.string().min(1),
    /** Present on customer sessions: the website account the customer is signed in as. */
    account_ref: z.string().min(1).nullable(),
    totp_enabled: z.boolean(),
    last_sign_in_at: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

/** Inferred type of {@link SessionSchema}. */
export type Session = z.infer<typeof SessionSchema>;

/** Step one of login: email + password. */
export const LoginRequestSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(1),
  })
  .strict();

/** Inferred type of {@link LoginRequestSchema}. */
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Step one's result. Operators are never signed in by password alone — SAD §11 makes TOTP
 * mandatory for the operator plane, so a correct password yields a challenge, not a session.
 */
export const LoginResponseSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('SESSION'), session: SessionSchema }).strict(),
  z
    .object({
      outcome: z.literal('TOTP_REQUIRED'),
      challenge_id: z.string().min(1),
      display_name: z.string().min(1),
    })
    .strict(),
]);

/** Inferred type of {@link LoginResponseSchema}. */
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** Step two of operator login: the six-digit TOTP code. */
export const TotpRequestSchema = z
  .object({
    challenge_id: z.string().min(1),
    code: z.string().regex(/^\d{6}$/, 'must be six digits'),
  })
  .strict();

/** Inferred type of {@link TotpRequestSchema}. */
export type TotpRequest = z.infer<typeof TotpRequestSchema>;

/**
 * Widen a Cashier Profile customer session into the console's own identity model.
 *
 * The console's shell has one notion of "who is signed in" spanning both planes; the profile has
 * a customer session and knows nothing about operators, roles or TOTP — correctly, since those
 * are PaymentFlow's concerns and the profile is the *website's* contract. This function is where
 * those two facts meet, and keeping it to one function is deliberate: it is the entire cost of
 * the console supporting a BFF that has never heard of an operator.
 */
export function sessionFromCustomer(customer: CustomerSession): Session {
  return {
    operator_id: null,
    display_name: customer.display_name,
    email: customer.email,
    area: 'customer',
    // RBAC is the operator plane's, and a customer holds no operator role. Not "no permissions" —
    // no role model at all.
    role: null,
    participant: customer.participant,
    account_ref: customer.customer_id,
    totp_enabled: false,
    last_sign_in_at: customer.signed_in_at,
  };
}

// ---------------------------------------------------------------------------------------------
// 2 · Dashboard metrics and the mock scenario — moved out
// ---------------------------------------------------------------------------------------------
//
// `DashboardMetrics`, `MetricSeries`, `MetricPoint` and `MockScenario` used to live here, with a
// note that SAD §13's KPIs were a Prometheus scrape rather than a JSON contract. `/ops/v1` serves
// them now (`routes/ops/dashboard.ts`), so they are contract shapes and live in `@pf/contracts`.
//
// Import them from there.

// ---------------------------------------------------------------------------------------------
// 2b/3 · Review-queue read model and payee accounts — moved out
// ---------------------------------------------------------------------------------------------
//
// `ReviewCaseContext`, `ReviewQueueRow`, `ReviewQueueProjection` and `PayeeAccount` used to be
// declared here, with a note explaining that `/ops/v1` had no endpoint for them. It does now
// (`routes/ops/triage.ts`, `routes/ops/admin-reads.ts`), so they are contract shapes rather than
// this console's private read models, and they live in `@pf/contracts` beside the rest of the
// operator API.
//
// That they were console-local for as long as they were is exactly why the operator area 404'd
// against a real deployment on every screen: nothing forced the two halves to agree, so they
// didn't. Import them from `@pf/contracts`.

// ---------------------------------------------------------------------------------------------
// 4 · The customer area's shapes — moved out
// ---------------------------------------------------------------------------------------------
//
// Balances, customer withdrawals and the activity feed used to be declared here. They now live in
// `@pf/bff-contract` (the Cashier Profile), because they are not this console's private business:
// they are the contract a participating website's BFF implements and any customer-facing UI —
// this one or a replacement — consumes. Leaving them here made them look like an implementation
// detail of one React app, which is exactly the impression that stops somebody writing their own.
//
// Import them from `@pf/bff-contract`.
